import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiClient, setOnUnauthorized } from '@/api';
import { queryClient } from '@/api/queryClient';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  setToken: (token: string | null) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = '@tinomobile:auth_token';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setToken = useCallback(async (newToken: string | null) => {
    setTokenState(newToken);
    if (newToken) {
      await AsyncStorage.setItem(TOKEN_KEY, newToken);
      // Invalidate all queries to refetch with new token
      queryClient.invalidateQueries();
    } else {
      await AsyncStorage.removeItem(TOKEN_KEY);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const storedToken = await AsyncStorage.getItem(TOKEN_KEY);
      setTokenState(storedToken);
    } catch (error) {
      console.error('Error checking auth:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.clearAuth();
      // Clear React Query cache to remove previous user data
      queryClient.clear();
      // Clear token - this will trigger navigation change via isAuthenticated
      setTokenState(null);
      await AsyncStorage.removeItem(TOKEN_KEY);
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Set up 401 handler to auto-logout
  useEffect(() => {
    setOnUnauthorized(() => {
      console.log('401 Unauthorized - logging out');
      logout();
    });
  }, [logout]);

  const value: AuthContextType = {
    isAuthenticated: !!token,
    isLoading,
    token,
    setToken,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
